import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import api from '../services/api';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const res = await api.get('/admin/dashboard');
      setData(res.data);
    } catch (err) {
      console.error('Error al cargar dashboard:', err);
      setError('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const bloquearUsuario = async (usuario) => {
    const motivo = prompt('Motivo del bloqueo:');
    if (!motivo) return;
    const congelar = window.confirm('Congelar saldo del usuario?');
    try {
      await api.post('/admin/bloquear-usuario', { usuario_id: usuario.id, motivo, congelar_saldo: congelar });
      alert('Usuario bloqueado exitosamente');
      cargarDatos();
    } catch (err) {
      alert('Error al bloquear: ' + (err.response?.data?.error || err.message));
    }
  };

  const desbloquearUsuario = async (usuario) => {
    if (!window.confirm('Desbloquear a este usuario?')) return;
    try {
      await api.post('/admin/desbloquear-usuario', { usuario_id: usuario.id });
      alert('Usuario desbloqueado');
      cargarDatos();
    } catch (err) {
      alert('Error al desbloquear: ' + (err.response?.data?.error || err.message));
    }
  };

  const liberarSospechoso = async (usuario) => {
    if (!window.confirm('Liberar de revision a este usuario?')) return;
    try {
      await api.post('/admin/liberar-sospechoso', { usuario_id: usuario.id });
      alert('Usuario liberado de revision');
      cargarDatos();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const validarUsuario = async (usuario) => {
    if (!window.confirm(`¿Validar a "${usuario.razon_social || usuario.email}"? Podrá generar guías de inmediato.`)) return;
    try {
      await api.post('/admin/validar-usuario', { usuario_id: usuario.id });
      // Actualizacion local: no re-descarga todo el dashboard.
      // usuarios se desestructura de data, asi que hay que actualizar data.
      setData((prev) => prev ? {
        ...prev,
        usuarios: prev.usuarios.map((x) => x.id === usuario.id ? { ...x, validado: 1 } : x)
      } : prev);
    } catch (err) {
      alert('Error al validar: ' + (err.response?.data?.error || err.message));
    }
  };

  // "En revision" pesa mas que "No validada": es una senal de fraude activa.
  const getEstadoUsuario = (u) => {
    if (u.bloqueado) return { label: 'Bloqueado', bg: '#fee2e2', color: '#991b1b' };
    if (u.sospechoso) return { label: 'En revision', bg: '#fef3c7', color: '#92400e' };
    if (u.validado === 0) return { label: 'No validada', bg: '#fef3c7', color: '#92400e' };
    return { label: 'Activo', bg: '#d1fae5', color: '#065f46' };
  };

  const formatMoney = (val) => `$${parseFloat(val || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
  const daysSince = (d) => d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fb' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', color: '#ff8c00', marginBottom: '0.5rem' }}>Cargando Dashboard...</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Obteniendo datos del sistema</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fb' }}>
        <div style={{ textAlign: 'center', color: '#dc2626' }}>
          <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>{error}</div>
          <button onClick={cargarDatos} style={{ padding: '0.5rem 1rem', backgroundColor: '#ff8c00', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { kpis, paqueterias, usuarios, enviosRecientes, alertasFraude } = data;
  const totalPaqueteriaEnvios = paqueterias.reduce((s, p) => s + p.total, 0);

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    padding: isMobile ? '1rem' : '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb'
  };

  const statusColor = (status) => {
    switch (status) {
      case 'ENTREGADO': return { bg: '#d1fae5', color: '#065f46' };
      case 'PENDIENTE': return { bg: '#fef3c7', color: '#92400e' };
      case 'CANCELADO': return { bg: '#fee2e2', color: '#991b1b' };
      default: return { bg: '#e5e7eb', color: '#374151' };
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fb', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto' }}>
      {/* Header */}
      <nav style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderBottom: '3px solid #ff8c00' }}>
        <div style={{ maxWidth: '90rem', margin: '0 auto', padding: isMobile ? '0 0.75rem' : '0 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', height: isMobile ? '3.5rem' : '4rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img src="/logo.png" alt="Logo" style={{ height: isMobile ? '5rem' : '10rem' }} />
              <span style={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: '600', color: '#ff8c00', backgroundColor: '#fff7ed', padding: '0.25rem 0.75rem', borderRadius: '9999px' }}>
                Admin
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button onClick={() => navigate('/dashboard')} style={{
                padding: '0.4rem 0.75rem', fontSize: '0.8rem', color: '#374151',
                backgroundColor: '#f3f4f6', border: 'none', borderRadius: '0.375rem', cursor: 'pointer'
              }}>Dashboard</button>
              <button onClick={() => navigate('/admin')} style={{
                padding: '0.4rem 0.75rem', fontSize: '0.8rem', color: '#374151',
                backgroundColor: '#f3f4f6', border: 'none', borderRadius: '0.375rem', cursor: 'pointer'
              }}>Panel Admin</button>
              <button onClick={() => navigate('/admin/clientes')} style={{
                padding: '0.4rem 0.75rem', fontSize: '0.8rem', color: 'white',
                backgroundColor: '#ff8c00', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: 600
              }}>Clientes</button>
              <button onClick={handleLogout} style={{
                padding: '0.4rem 0.75rem', fontSize: '0.8rem', color: '#6b7280',
                backgroundColor: 'transparent', border: 'none', cursor: 'pointer'
              }}>Salir</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '90rem', margin: '0 auto', padding: isMobile ? '1rem 0.75rem' : '1.5rem' }}>
        {/* Title */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: isMobile ? '1.5rem' : '1.75rem', fontWeight: '700', color: '#111827', margin: 0 }}>
            Admin Dashboard
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            Resumen general del sistema
          </p>
        </div>

        {/* Alertas Anti-Fraude */}
        {alertasFraude && alertasFraude.length > 0 && (
          <div style={{
            ...cardStyle, marginBottom: '1.5rem',
            borderLeft: '4px solid #ef4444', backgroundColor: '#fef2f2'
          }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#991b1b', marginTop: 0, marginBottom: '0.75rem' }}>
              Usuarios con muchos envios de 1kg esta semana
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {alertasFraude.map((a) => (
                <div key={a.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.75rem', backgroundColor: 'white', borderRadius: '0.5rem',
                  border: '1px solid #fecaca', flexWrap: 'wrap', gap: '0.5rem'
                }}>
                  <div>
                    <span style={{ fontWeight: '600', color: '#111827' }}>{a.nombre || a.email}</span>
                    <span style={{ color: '#6b7280', fontSize: '0.85rem', marginLeft: '0.5rem' }}>{a.email}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.2rem 0.6rem',
                      borderRadius: '9999px', fontSize: '0.8rem', fontWeight: '600'
                    }}>{a.envios_ligeros} envios ligeros</span>
                    <button onClick={() => {
                      const usr = usuarios.find(u => u.id === a.id);
                      if (usr) bloquearUsuario(usr);
                    }} style={{
                      padding: '0.35rem 0.75rem', fontSize: '0.8rem', backgroundColor: '#ef4444',
                      color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600'
                    }}>Revisar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPI Cards - Main Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? '0.75rem' : '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ ...cardStyle, borderLeft: '4px solid #ff8c00' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.05em' }}>Envios Totales</div>
            <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '700', color: '#111827', marginTop: '0.25rem' }}>{kpis.totalEnvios}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Hoy: {kpis.enviosHoy} | Mes: {kpis.enviosMes}</div>
          </div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #10b981' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.05em' }}>Ingresos Totales</div>
            <div style={{ fontSize: isMobile ? '1.25rem' : '2rem', fontWeight: '700', color: '#10b981', marginTop: '0.25rem' }}>{formatMoney(kpis.ingresosTotales)}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Mes: {formatMoney(kpis.ingresosMes)}</div>
          </div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #ef4444' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.05em' }}>Costos Totales</div>
            <div style={{ fontSize: isMobile ? '1.25rem' : '2rem', fontWeight: '700', color: '#ef4444', marginTop: '0.25rem' }}>{formatMoney(kpis.costosTotales)}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Mes: {formatMoney(kpis.costosMes)}</div>
          </div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.05em' }}>Ganancia (Comision)</div>
            <div style={{ fontSize: isMobile ? '1.25rem' : '2rem', fontWeight: '700', color: '#8b5cf6', marginTop: '0.25rem' }}>{formatMoney(kpis.gananciaTotales)}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>Mes: {formatMoney(kpis.gananciaMes)}</div>
          </div>
        </div>

        {/* KPI Cards - Secondary Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: isMobile ? '0.75rem' : '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Usuarios</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginTop: '0.25rem' }}>{kpis.totalUsuarios}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{kpis.totalEmpresas} empresas</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Pendientes</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f59e0b', marginTop: '0.25rem' }}>{kpis.enviosPendientes}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Requieren atencion</div>
          </div>
          <div style={{ ...cardStyle, gridColumn: isMobile ? 'span 2' : 'auto' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>Entregados / Cancelados</div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>{kpis.enviosEntregados}</span>
              <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444' }}>{kpis.enviosCancelados}</span>
            </div>
          </div>
        </div>

        {/* Carrier Breakdown */}
        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginTop: 0, marginBottom: '1rem' }}>
            Desglose por Paqueteria
          </h2>
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {paqueterias.map((p) => (
                <div key={p.paqueteria} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: '600', color: '#111827' }}>{p.paqueteria || 'Sin paqueteria'}</span>
                    <span style={{ fontWeight: '600', color: '#ff8c00' }}>{p.total} envios</span>
                  </div>
                  <div style={{ backgroundColor: '#f3f4f6', borderRadius: '9999px', height: '0.5rem', marginBottom: '0.5rem' }}>
                    <div style={{ backgroundColor: '#ff8c00', height: '100%', borderRadius: '9999px', width: `${totalPaqueteriaEnvios ? (p.total / totalPaqueteriaEnvios * 100) : 0}%` }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280' }}>
                    <span>Ingreso: {formatMoney(p.ingresos)}</span>
                    <span>Ganancia: {formatMoney(p.ganancia)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Paqueteria</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Envios</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', width: '25%' }}>Distribucion</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Ingresos</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Costos</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Ganancia</th>
                  </tr>
                </thead>
                <tbody>
                  {paqueterias.map((p, i) => {
                    const pct = totalPaqueteriaEnvios ? (p.total / totalPaqueteriaEnvios * 100) : 0;
                    return (
                      <tr key={p.paqueteria} style={{ borderBottom: i < paqueterias.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#111827' }}>{p.paqueteria || 'Sin paqueteria'}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600', color: '#ff8c00' }}>{p.total}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{ flex: 1, backgroundColor: '#f3f4f6', borderRadius: '9999px', height: '0.5rem' }}>
                              <div style={{ backgroundColor: '#ff8c00', height: '100%', borderRadius: '9999px', width: `${pct}%`, transition: 'width 0.3s' }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '2.5rem' }}>{pct.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#111827' }}>{formatMoney(p.ingresos)}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#ef4444' }}>{formatMoney(p.costos)}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#10b981', fontWeight: '600' }}>{formatMoney(p.ganancia)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginTop: 0, marginBottom: '1rem' }}>
            Usuarios ({usuarios.length})
          </h2>
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {usuarios.map((u) => {
                const estado = getEstadoUsuario(u);
                return (
                  <div key={u.id} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', backgroundColor: u.bloqueado ? '#fef2f2' : u.sospechoso ? '#fffbeb' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{u.razon_social || 'Sin empresa'}</div>
                      <span style={{ padding: '0.15rem 0.5rem', fontSize: '0.65rem', borderRadius: '9999px', backgroundColor: estado.bg, color: estado.color, fontWeight: '600' }}>
                        {estado.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>{u.email}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                      <span style={{ color: '#6b7280' }}>Envios: <strong>{u.total_envios}</strong></span>
                      <span style={{ color: '#6b7280' }}>Saldo: <strong style={{ color: '#10b981' }}>{formatMoney(u.saldo)}</strong>{u.saldo_congelado === 1 && ' 🔒'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.5rem' }}>
                      {!u.bloqueado && (
                        <button onClick={() => bloquearUsuario(u)} style={{
                          padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#ef4444',
                          color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                        }}>Bloquear</button>
                      )}
                      {u.bloqueado === 1 && (
                        <button onClick={() => desbloquearUsuario(u)} style={{
                          padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#10b981',
                          color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                        }}>Desbloquear</button>
                      )}
                      {u.sospechoso === 1 && (
                        <button onClick={() => liberarSospechoso(u)} style={{
                          padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#3b82f6',
                          color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                        }}>Liberar</button>
                      )}
                      {!u.bloqueado && u.validado === 0 && (
                        <button onClick={() => validarUsuario(u)} style={{
                          padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#ff8c00',
                          color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                        }}>Validar</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Empresa</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Email</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Estado</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Envios</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Saldo</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Registro</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u, i) => {
                    const estado = getEstadoUsuario(u);
                    return (
                      <tr key={u.id} style={{ borderBottom: i < usuarios.length - 1 ? '1px solid #f3f4f6' : 'none', backgroundColor: u.bloqueado ? '#fef2f2' : u.sospechoso ? '#fffbeb' : 'transparent' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#111827' }}>{u.razon_social || 'Sin empresa'}</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>{u.email}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                          <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.7rem', borderRadius: '9999px', backgroundColor: estado.bg, color: estado.color, fontWeight: '600' }}>
                            {estado.label}
                          </span>
                          {u.sospechoso === 1 && !u.bloqueado && (
                            <span style={{ marginLeft: '0.25rem', fontSize: '0.8rem' }} title="Sospechoso">⚠️</span>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600', color: '#ff8c00' }}>{u.total_envios}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '500', color: '#10b981' }}>
                          {formatMoney(u.saldo)}
                          {u.saldo_congelado === 1 && <span style={{ marginLeft: '0.25rem', fontSize: '0.8rem' }} title="Saldo congelado">🔒</span>}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', color: '#6b7280' }}>{formatDate(u.created_at)}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {!u.bloqueado && (
                              <button onClick={() => bloquearUsuario(u)} style={{
                                padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#ef4444',
                                color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                              }}>Bloquear</button>
                            )}
                            {u.bloqueado === 1 && (
                              <button onClick={() => desbloquearUsuario(u)} style={{
                                padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#10b981',
                                color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                              }}>Desbloquear</button>
                            )}
                            {u.sospechoso === 1 && (
                              <button onClick={() => liberarSospechoso(u)} style={{
                                padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#3b82f6',
                                color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                              }}>Liberar</button>
                            )}
                            {!u.bloqueado && u.validado === 0 && (
                              <button onClick={() => validarUsuario(u)} style={{
                                padding: '0.3rem 0.6rem', fontSize: '0.7rem', backgroundColor: '#ff8c00',
                                color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontWeight: '600'
                              }}>Validar</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Shipments */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginTop: 0, marginBottom: '1rem' }}>
            Envios Recientes
          </h2>
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {enviosRecientes.map((e) => {
                const sc = statusColor(e.status);
                return (
                  <div key={e.id} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#111827' }}>{e.numero_guia || 'Sin guia'}</span>
                      <span style={{ padding: '0.15rem 0.5rem', fontSize: '0.7rem', borderRadius: '9999px', backgroundColor: sc.bg, color: sc.color, fontWeight: '600' }}>{e.status}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{e.cliente_nombre || 'N/A'}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                      <span style={{ color: '#6b7280' }}>{e.paqueteria}</span>
                      <span style={{ fontWeight: '600', color: '#111827' }}>{formatMoney(e.costo_cliente)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                      <span>{e.peso ? parseFloat(e.peso) + ' kg' : '-'} | {e.largo && e.ancho && e.alto ? `${parseFloat(e.largo)}x${parseFloat(e.ancho)}x${parseFloat(e.alto)} cm` : '-'}</span>
                      <span>{formatDate(e.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Guia</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Cliente</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Paqueteria</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Destino</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Peso</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Dimensiones</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Costo</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Estado</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {enviosRecientes.map((e, i) => {
                    const sc = statusColor(e.status);
                    return (
                      <tr key={e.id} style={{ borderBottom: i < enviosRecientes.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#111827', fontSize: '0.875rem' }}>{e.numero_guia || 'Sin guia'}</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>{e.cliente_nombre || 'N/A'}</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>{e.paqueteria}</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>{e.destino_ciudad}{e.destino_estado ? `, ${e.destino_estado}` : ''}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.85rem', color: '#111827', fontWeight: '500' }}>{e.peso ? parseFloat(e.peso) + ' kg' : '-'}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', color: '#6b7280' }}>{e.largo && e.ancho && e.alto ? `${parseFloat(e.largo)}x${parseFloat(e.ancho)}x${parseFloat(e.alto)}` : '-'}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '500', color: '#111827' }}>{formatMoney(e.costo_cliente)}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                          <span style={{ padding: '0.2rem 0.6rem', fontSize: '0.7rem', borderRadius: '9999px', backgroundColor: sc.bg, color: sc.color, fontWeight: '600' }}>{e.status}</span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.8rem', color: '#6b7280' }}>{formatDate(e.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer spacer */}
        <div style={{ height: '2rem' }} />
      </div>
    </div>
  );
}

export default AdminDashboard;
